import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Drawer,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AdminPage from '../../components/admin/AdminPage';
import SectionCard from '../../components/admin/SectionCard';
import { LoadingState } from '../../components/admin/StateViews';
import StatusChip from '../../components/admin/StatusChip';
import { AdminUser, UserStatus } from '../../types';
import { AdminUserService } from '../../services/AdminUserService';

const PAGE_SIZE = 10;

const formatPoints = (value: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);

const UserManagementPage = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | UserStatus>('all');
  const [roleFilter, setRoleFilter] = useState<'all' | string>('all');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      setUsers(await AdminUserService.getUsers());
    } catch (loadError) {
      console.error(loadError);
      setError('Failed to load users from the backend directory.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const roleOptions = useMemo(() => {
    const labels = users
      .map((user) => user.roleLabel)
      .filter((roleLabel): roleLabel is string => Boolean(roleLabel));

    return Array.from(new Set(labels)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return users.filter((adminUser) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        adminUser.name.toLowerCase().includes(normalizedSearch) ||
        adminUser.email.toLowerCase().includes(normalizedSearch) ||
        adminUser.roleLabel.toLowerCase().includes(normalizedSearch) ||
        adminUser.roles.some((role) =>
          role.toLowerCase().includes(normalizedSearch)
        );
      const matchesStatus =
        statusFilter === 'all' || adminUser.status === statusFilter;
      const matchesRole =
        roleFilter === 'all' || adminUser.roleLabel === roleFilter;

      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [roleFilter, search, statusFilter, users]);

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, roleFilter]);

  if (loading) {
    return <LoadingState label="Loading users…" />;
  }

  return (
    <AdminPage
      title="User Management"
      description="Search, filter, and review real users synced from Cognito and the app backend. This directory now reflects backend data instead of local placeholder users."
    >
      {error ? <Alert severity="error">{error}</Alert> : null}

      <SectionCard
        title="User directory"
        description="Use filters to review real application users, their backend status, and their synced Cognito roles."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField
              label="Search users"
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3.5}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(event) =>
                  setStatusFilter(event.target.value as 'all' | UserStatus)
                }
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3.5}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(event) => setRoleFilter(event.target.value)}
              >
                <MenuItem value="all">All roles</MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem value={role} key={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Points</TableCell>
                <TableCell>Last active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((adminUser) => (
                <TableRow key={adminUser.id} hover>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography fontWeight={600}>{adminUser.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {adminUser.email}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{adminUser.roleLabel}</TableCell>
                  <TableCell>
                    <StatusChip value={adminUser.status} />
                  </TableCell>
                  <TableCell>{formatPoints(adminUser.pointsBalance)}</TableCell>
                  <TableCell>
                    {new Date(adminUser.lastActiveAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Button variant="text" onClick={() => setSelectedUser(adminUser)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {filteredUsers.length} users found
          </Typography>
          <Pagination
            page={page}
            count={pageCount}
            onChange={(_, nextPage) => setPage(nextPage)}
            color="primary"
          />
        </Stack>
      </SectionCard>

      <Drawer
        anchor="right"
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 3 } }}
      >
        {selectedUser ? (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5">{selectedUser.name}</Typography>
              <Typography color="text.secondary">{selectedUser.email}</Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <StatusChip value={selectedUser.status} />
              <StatusChip value={selectedUser.roleLabel} />
              {selectedUser.roles.map((role) => (
                <StatusChip key={role} value={role} />
              ))}
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Joined
                </Typography>
                <Typography>
                  {new Date(selectedUser.joinedAt).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Last active
                </Typography>
                <Typography>
                  {new Date(selectedUser.lastActiveAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Points balance
                </Typography>
                <Typography>{formatPoints(selectedUser.pointsBalance)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Synced roles
                </Typography>
                <Typography>
                  {selectedUser.roles.length > 0
                    ? selectedUser.roles.join(', ')
                    : 'No explicit roles'}
                </Typography>
              </Grid>
            </Grid>
            <Typography color="text.secondary">{selectedUser.notes}</Typography>

            <Button variant="outlined" onClick={() => setSelectedUser(null)}>
              Close
            </Button>
          </Stack>
        ) : null}
      </Drawer>
    </AdminPage>
  );
};

export default UserManagementPage;
